// Accessible custom listbox (ported from the source prototype's OptionSelect.tsx):
// click-to-open, outside-click + Escape to close, dispatches
// 'option-select:change' with the selected option's dataset.
//
// Every handler is bound ONCE, in the constructor, and always registered by that
// same reference. addEventListener ignores a duplicate registration of an
// identical reference, so connectedCallback can run any number of times without
// stacking listeners. It used to add a fresh arrow function on every connect,
// and connectedCallback fires again whenever the element is moved or its section
// is re-rendered — two click listeners meant one click ran toggle() twice, which
// opened and closed in the same gesture and left the list stuck open with no way
// to shut it.
class OptionSelect extends HTMLElement {
  constructor() {
    super();
    this.onTriggerClick = () => this.toggle();
    this.onTriggerKeydown = (event) => this.handleTriggerKeydown(event);
    // Delegated rather than one listener per option: duplicate-proof, and it
    // survives the options being re-rendered under the same element.
    this.onListboxClick = (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.select(this.options.indexOf(option));
    };
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.onDocumentKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen()) {
        this.close();
        this.trigger?.focus();
      }
    };
  }

  connectedCallback() {
    this.trigger = this.querySelector('.option-select__trigger');
    this.listbox = this.querySelector('[role="listbox"]');
    this.options = Array.from(this.querySelectorAll('[role="option"]'));
    this.hiddenInput = this.querySelector('input[type="hidden"]');
    this.selectedLabel = this.querySelector('[data-selected-label]');
    this.activeIndex = this.options.findIndex((el) => el.getAttribute('aria-selected') === 'true');
    if (this.activeIndex < 0) this.activeIndex = 0;

    this.trigger?.addEventListener('click', this.onTriggerClick);
    this.trigger?.addEventListener('keydown', this.onTriggerKeydown);
    this.listbox?.addEventListener('click', this.onListboxClick);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
  }

  // The document-level pair has to go, or a detached instance keeps reacting to
  // every click on the page. The element's own listeners leave with it.
  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  isOpen() {
    return this.trigger?.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  open() {
    this.trigger.setAttribute('aria-expanded', 'true');
    this.listbox.hidden = false;
  }

  close() {
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (this.listbox) this.listbox.hidden = true;
  }

  // Arrow keys only. Enter and Space are deliberately NOT handled: a <button>
  // already turns them into a click, which toggles. Calling open() here and
  // preventing the default click meant those two keys could only ever open the
  // list — pressing Enter again did nothing, so a keyboard user could not shut
  // it.
  handleTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.open();
    }
  }

  select(index) {
    const option = this.options[index];
    if (!option) return;

    this.options.forEach((el) => el.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');
    this.activeIndex = index;

    if (this.selectedLabel) this.selectedLabel.textContent = option.dataset.label || option.textContent.trim();
    if (this.hiddenInput) this.hiddenInput.value = option.dataset.value || '';

    this.classList.remove('option-select--invalid');
    this.close();
    this.trigger?.focus();

    this.dispatchEvent(
      new CustomEvent('option-select:change', {
        bubbles: true,
        // `label` and `period` let a listener build a price summary line without
        // reaching back into the DOM for the option it just picked.
        detail: {
          value: option.dataset.value,
          price: option.dataset.price,
          label: option.dataset.label || option.textContent.trim(),
          period: option.dataset.period,
          name: this.dataset.name,
        },
      })
    );
  }

  markInvalid() {
    this.classList.add('option-select--invalid');
  }
}

if (!customElements.get('option-select')) {
  customElements.define('option-select', OptionSelect);
}
